// After a recipe or ingredient save rewrites products' ingredient lists and
// allergens, publish each affected flavor's products to the destinations
// ticked under Publish To on those products (Zoho, B2B/Web Woo, Faire), a
// few flavors at a time (to stay under Zoho and Woo rate limits),
// with a progress toast ("Publishing 3 of 12 flavors…"). Runs after the
// dialog closes; a flavor that fails is named and the rest carry on.

import { toast } from '../lib/shadcn/sonner'
import { describeProblems, destinationName, listNames, type SyncProblem } from './syncResults'

type PublishTrigger = (params: { label_name: string }) => Promise<unknown>

type PublishResult = { destinations?: unknown; problems?: unknown; published?: unknown }

// Flavors published at the same time. Each one already sends Zoho, B2B and
// Web in parallel, so this keeps it to about a dozen calls in flight.
const AT_ONCE = 4

export async function publishLabels(labels: string[], trigger: PublishTrigger): Promise<void> {
  const unique = [...new Set(labels)]
  if (unique.length === 0) return
  const id = toast.loading(`Publishing ${unique[0]} to its Publish To destinations…`)
  const failed: string[] = []
  const notSet: string[] = []
  const problems: SyncProblem[] = []
  const reached = new Set<string>()
  let finished = 0
  let next = 0
  const progress = () => {
    if (unique.length > 1) {
      toast.loading(
        `Publishing ${unique.length} flavors to their Publish To destinations (${finished} of ${unique.length} done)…`,
        { id },
      )
    }
  }
  progress()
  async function publishOne(label: string) {
    try {
      const res = ((await trigger({ label_name: label })) ?? {}) as PublishResult
      const published = Array.isArray(res.published) ? res.published : []
      if (published.length === 0) notSet.push(label)
      if (Array.isArray(res.destinations)) for (const d of res.destinations) reached.add(String(d))
      if (Array.isArray(res.problems)) {
        for (const p of res.problems as SyncProblem[]) problems.push(p)
      }
    } catch {
      failed.push(label)
    }
    finished++
    if (finished < unique.length) progress()
  }
  async function worker() {
    while (next < unique.length) await publishOne(unique[next++]!)
  }
  await Promise.all(Array.from({ length: Math.min(AT_ONCE, unique.length) }, () => worker()))
  // Name flavors in the order they were saved, not the order they finished.
  const byOrder = (a: string, b: string) => unique.indexOf(a) - unique.indexOf(b)
  failed.sort(byOrder)
  notSet.sort(byOrder)

  const parts: string[] = []
  const done = unique.length - failed.length - notSet.length
  if (done > 0 && reached.size > 0) {
    parts.push(
      `${done === 1 && unique.length === 1 ? unique[0] : `${done} flavor${done === 1 ? '' : 's'}`} published to ` +
        listNames([...reached].map(destinationName)) +
        '.',
    )
  }
  if (notSet.length > 0) {
    parts.push(
      `${listNames(notSet)} ${notSet.length === 1 ? 'has' : 'have'} nothing ticked under Publish To, so only the Hub was updated.`,
    )
  }
  if (problems.length > 0) parts.push(describeProblems(problems))
  if (failed.length > 0) {
    parts.push(`Couldn't publish ${listNames(failed)}. Save ${failed.length === 1 ? 'it' : 'them'} from Inventory to retry.`)
  }
  const message = parts.join(' ') || 'Ingredient lists updated in the Hub.'
  if (failed.length > 0 || problems.some(p => p.status === 'failed')) toast.error(message, { id, duration: 20000 })
  else if (problems.length > 0 || notSet.length > 0) toast.warning(message, { id, duration: 15000 })
  else toast.success(message, { id })
}
